import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../slices/authSlice';
import Metrics from './cards/Metrics';
import ProjectsCard from './cards/ProjectsCard';
import AttentionCard from './cards/AttentionCard';
import MemoryCard from './cards/MemoryCard';
import CalendarCard from './cards/CalendarCard';
import ActivityCard from './cards/ActivityCard';
import IntegrationsCard from './cards/IntegrationsCard';

const greeting = () => {
    const h = new Date().getHours();
    if (h < 5) return 'Late night';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
};

const DashboardHome = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((s) => s.auth.token);
    const reduxUser = useSelector((s) => s.auth.user) || {};

    const [filter, setFilter] = useState('All');
    const [range, setRange] = useState('Today');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [projects, setProjects] = useState([]);
    const [team, setTeam] = useState([]);
    const [metrics, setMetrics] = useState({});
    const [attention, setAttention] = useState([]);
    const [memory, setMemory] = useState({ term: '', filter: '', matches: [] });
    const [calendar, setCalendar] = useState([]);
    const [activity, setActivity] = useState([]);
    const [integrations, setIntegrations] = useState([]);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const fetchOpt = (url, fallback) => axios.get(url, { headers }).then((r) => r.data).catch(() => fallback);

        Promise.all([
            fetchOpt('/api/projects?summary=true', {}),
            fetchOpt('/api/team', []),
            fetchOpt(`/api/dashboard/metrics?range=${range.toLowerCase()}`, {}),
            fetchOpt('/api/dashboard/attention', []),
            fetchOpt('/api/dashboard/memory-matches', { term: '', filter: '', matches: [] }),
            fetchOpt('/api/dashboard/calendar', []),
            fetchOpt('/api/dashboard/activity', []),
            fetchOpt('/api/dashboard/integrations', []),
        ])
            .then(([projRes, teamRes, metricsRes, attRes, memRes, calRes, actRes, intRes]) => {
                const { createdProjects = [], sharedProjects = [] } = projRes || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                setProjects([...unique.values()]);
                setTeam(Array.isArray(teamRes) ? teamRes : []);
                setMetrics(metricsRes || {});
                setAttention(Array.isArray(attRes) ? attRes : []);
                setMemory(memRes || { term: '', filter: '', matches: [] });
                setCalendar(Array.isArray(calRes) ? calRes : []);
                setActivity(Array.isArray(actRes) ? actRes : []);
                setIntegrations(Array.isArray(intRes) ? intRes : []);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load dashboard data.');
                    setLoading(false);
                }
            });
    }, [token, range, dispatch, navigate]);

    const activeProjects = useMemo(() => projects.filter((p) => !p.completed).length, [projects]);
    const languageCount = useMemo(() => {
        const set = new Set();
        projects.forEach((p) => (p.targetLanguages || []).forEach((l) => set.add(l.code || l)));
        return set.size;
    }, [projects]);
    const reviewCount = attention.length;

    const firstName = reduxUser.firstName || (reduxUser.email || '').split('@')[0] || 'there';
    const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

    if (loading) {
        return (
            <div style={{ padding: 40, color: 'var(--muted, #6b6a66)' }}>Loading your workspace…</div>
        );
    }
    if (error) {
        return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    }

    return (
        <>
            <div className="rs-page-head">
                <div>
                    <h1 className="rs-greeting">
                        {greeting()}, <span className="accent">{firstName}</span>.
                    </h1>
                    <p className="rs-greeting-sub">
                        {today} · {activeProjects} active projects across {languageCount} languages · {reviewCount} awaiting your review.
                    </p>
                </div>
                <div className="rs-segmented" role="tablist">
                    {['Today', 'Week', 'Month', 'Quarter'].map((r) => (
                        <button
                            type="button"
                            key={r}
                            className={r === range ? 'on' : ''}
                            onClick={() => setRange(r)}
                        >
                            {r}
                        </button>
                    ))}
                </div>
            </div>

            <Metrics data={metrics} />

            <div className="rs-grid">
                <ProjectsCard projects={projects} filter={filter} setFilter={setFilter} userId={reduxUser._id} />
                <AttentionCard items={attention} />
            </div>

            <div className="rs-grid">
                <MemoryCard matches={memory.matches} term={memory.term} filter={memory.filter} />
                <CalendarCard tasks={calendar} />
            </div>

            <div className="rs-grid">
                <ActivityCard items={activity} />
                <IntegrationsCard items={integrations} />
            </div>
        </>
    );
};

export default DashboardHome;
